import { Button, Icon } from '@platform/components';
import { Link } from 'wouter';
import dataTestids from '../constants/test-ids';
import NavbarDropdown, { NavbarDropdownItem } from '../dashboard/NavbarDropdown';
import { ROUTES } from '../routes/consts';
import { MovingWorldsBannerLogo } from './MovingWorldsLogo';

const aboutUsItems: NavbarDropdownItem[] = [
  {
    title: 'Our story',
    href: '/about',
    linkType: 'external',
  },
  {
    title: 'Meet the team',
    href: '/about#get-to-know-the-team',
    linkType: 'external',
  },
  {
    title: 'Get in touch',
    href: '/contact-form-details?no_cache=1',
    linkType: 'external',
  },
];

const whatWeOfferItems: NavbarDropdownItem[] = [
  {
    title: 'Social Enterprises',
    href: ROUTES.SOCIAL_ENTERPRISES,
    linkType: 'internal',
  },
  {
    title: 'Professionals',
    href: ROUTES.PROFESSIONALS,
    linkType: 'internal',
  },
  {
    title: 'Corporates',
    href: '/skills-based-volunteering-platform',
    linkType: 'external',
  },
  {
    title: 'Impact Partners',
    href: '/impact-partners',
    linkType: 'external',
  },
];

const impactItems: NavbarDropdownItem[] = [
  {
    title: 'Living Impact Report',
    href: '/impact-of-experteering',
    linkType: 'external',
  },
  {
    title: 'Network Report',
    href: '/network-report',
    linkType: 'external',
  },
];

const resourcesItems: NavbarDropdownItem[] = [
  {
    title: 'Blog',
    href: 'https://blog.movingworlds.org',
    linkType: 'external',
  },
  {
    title: 'Guides',
    href: '/resources-and-guides',
    linkType: 'external',
  },
];

const UnauthedNavbarDropdown = ({
  buttonText,
  items,
  dataTestId,
}: {
  buttonText: string;
  items: NavbarDropdownItem[];
  dataTestId: string;
}) => {
  return (
    <NavbarDropdown
      buttonText={buttonText}
      items={items}
      isLoading={false}
      additionalTitleClassnames='text-xl md:text-sm'
      buttonPropsOverride={{
        size: 'lg',
        variant: 'primary',
        additionalClassnames:
          'md:text-white hover:text-gray-100 md:bg-brand-500 md:hover:bg-brand-500 border-none focus:none whitespace-nowrap text-sm',
        children: buttonText,
      }}
      dataTestId={dataTestId}
    />
  );
};

export const UnauthedNavbar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  sidebarRef,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => {
  const navBarSection = (
    <>
      <UnauthedNavbarDropdown
        buttonText='About us'
        items={aboutUsItems}
        dataTestId={dataTestids.LAYOUT.HEADER.ABOUT_US}
      />
      <UnauthedNavbarDropdown
        buttonText='What we offer'
        items={whatWeOfferItems}
        dataTestId={dataTestids.LAYOUT.HEADER.WHAT_WE_OFFER}
      />
      <UnauthedNavbarDropdown
        buttonText='Impact'
        items={impactItems}
        dataTestId={dataTestids.LAYOUT.HEADER.IMPACT}
      />
      <UnauthedNavbarDropdown
        buttonText='Resources'
        items={resourcesItems}
        dataTestId={dataTestids.LAYOUT.HEADER.RESOURCES}
      />
    </>
  );

  const loginSection = (
    <>
      <div className='p-1 md:p-0 w-[83px] flex md:justify-center md:text-center'>
        <Link
          className='text-xl md:text-base font-medium w-full'
          href={`~/platform${ROUTES.AUTH}${ROUTES.LOGIN}`}
        >
          Log In
        </Link>
      </div>
      <Button
        variant='tertiary-color'
        size='xl'
        href={`platform${ROUTES.AUTH}${ROUTES.SIGN_UP}`}
        additionalClassnames='p-1 md:p-0 w-full md:w-[69px] text-gray-500 border-none bg-accent-500 active:none focus:none hover:bg-accent-500'
      >
        Join
      </Button>
    </>
  );

  return (
    <div
      className='z-40 sticky top-0 md:relative md:top-auto bg-brand-500 h-[54px] md:h-[var(--navbar-height)] px-4 lg:px-10 py-6 flex items-center shadow-sm'
      data-testid={dataTestids.LAYOUT.UNAUTHED_NAVBAR}
    >
      <div className='text-white flex flex-row justify-between w-full'>
        {/* Lazy fix for bad SVG sizing; need to re-export the SVG */}
        <a className='flex items-center' href='/' data-testid={dataTestids.LAYOUT.HEADER.LOGO}>
          {/* The legacy app uses a different size around roughly the 'md' breakpoint here, but this has been ignored in favor of us redefining how we want to handle mobile responsiveness in the future */}
          <div className='w-[200px] lg:w-[224px]'>
            <MovingWorldsBannerLogo />
          </div>
        </a>
        <div className='hidden md:flex flex-row gap-4 items-center ml-4'>
          {navBarSection}
          {loginSection}
        </div>

        {isSidebarOpen && (
          <div
            className='md:hidden fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 z-50 text-gray-500 text-md'
            data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL}
          >
            <div
              ref={sidebarRef}
              className='fixed top-0 left-0 overflow-y-auto w-[calc(100vw-64px)] h-screen bg-white z-50 p-2'
            >
              {navBarSection}
              {loginSection}
            </div>
          </div>
        )}
        <button
          className='flex md:hidden cursor-pointer mr-[-12px] p-4'
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL_BUTTON}
        >
          <Icon type='menu-02' size='24' stroke='white' />
        </button>
      </div>
    </div>
  );
};
